import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { QrCodeIcon, Mail, Phone, User, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiClient } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function AuthPage() {
  // ⚠️ IMPORTANT: ALL hooks MUST be called before any conditional returns
  // to avoid "Rendered fewer hooks than expected" error
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [activeTab, setActiveTab] = useState('link');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [errorLog, setErrorLog] = useState(() => {
    // Load error log from localStorage on init
    try {
      const saved = localStorage.getItem('authErrorLog');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [showErrorLog, setShowErrorLog] = useState(true);
  const [lastError, setLastError] = useState('');
  const { user, login } = useAuth();
  const navigate = useNavigate();
  
  // Helper function to log errors persistently
  const logError = (message, details = {}) => {
    const timestamp = new Date().toLocaleTimeString();
    const errorEntry = {
      timestamp,
      message,
      details: JSON.stringify(details, null, 2)
    };
    console.error(`[${timestamp}] ${message}`, details);
    const newLog = [...errorLog, errorEntry];
    setErrorLog(newLog);
    setShowErrorLog(true);
    setLastError(message);
    // Persist to localStorage
    try {
      localStorage.setItem('authErrorLog', JSON.stringify(newLog.slice(-10))); // Keep last 10 errors
    } catch (e) {
      console.error('Failed to save error log', e);
    }
  };
  
  // Clear error log function
  const clearErrorLog = () => {
    setErrorLog([]);
    setLastError('');
    try {
      localStorage.removeItem('authErrorLog');
    } catch (e) {
      console.error('Failed to clear error log', e);
    }
  };

  // Redirect if already logged in - check role for proper redirect
  // This MUST come after all hooks
  if (user) {
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  const handleAuthLinkRequest = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const formData = new FormData(event.target);
    const identifier = formData.get('identifier');

    try {
      await api.requestAuthLink(identifier);
      setEmailSent(true);
      toast.success('Authentication link sent! Check your email or SMS.');
    } catch (error) {
      console.error('Auth link request failed:', error);
      toast.error('Failed to send authentication link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async (event) => {
    event.preventDefault();
    console.log("=== SIGN IN ATTEMPT STARTED ===");
    console.log("Form submitted at:", new Date().toISOString());
    setIsLoading(true);

    const formData = new FormData(event.target);
    const identifier = formData.get('identifier');
    const password = formData.get('password');

    console.log("Login credentials:", { identifier, passwordLength: password?.length });
    console.log("Password first/last chars:", { 
      first: password?.charAt(0), 
      last: password?.charAt(password?.length - 1),
      actual: password  // TEMPORARY: Log actual password for debugging
    });
    
    logError('Login attempt started', { identifier });

    try {
      logError('Calling API login endpoint...', { 
        url: `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`,
        identifier 
      });
      
      const response = await api.loginWithPassword(identifier, password);
      logError('Login API response received', { 
        status: response.status,
        hasToken: !!response.data.access_token 
      });
      
      const accessToken = response.data.access_token;
      
      // Set token and authorization header
      login(accessToken);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      logError('Token set, fetching user profile...');
      
      // Fetch user profile BEFORE navigating
      const profileResponse = await api.getUserProfile();
      const userProfile = profileResponse.data;
      
      logError('User profile loaded successfully', { 
        username: userProfile.username,
        role: userProfile.role 
      });
      
      // Now navigate based on role
      if (userProfile.role === 'admin') {
        toast.success('Welcome to admin panel!');
        navigate('/admin', { replace: true });
      } else {
        toast.success('Signed in successfully!');
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      // Extract detailed error message
      let errorMessage = 'Invalid credentials. Please try again.';
      let errorDetails = {
        errorType: typeof error,
        errorKeys: Object.keys(error || {})
      };
      
      if (error.response) {
        // Server responded with error
        errorDetails = {
          ...errorDetails,
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          headers: error.response.headers
        };
        
        errorMessage = error.response.data?.detail || 
                      error.response.data?.message || 
                      `Server error (${error.response.status})`;
      } else if (error.request) {
        // Request made but no response
        errorDetails = {
          ...errorDetails,
          noResponse: true,
          requestUrl: error.config?.url,
          requestMethod: error.config?.method,
          requestHeaders: error.config?.headers
        };
        errorMessage = 'Cannot reach server. Please check your connection.';
      } else {
        // Error setting up request
        errorDetails = {
          ...errorDetails,
          setupError: true,
          message: error.message
        };
        errorMessage = 'Network error: ' + error.message;
      }
      
      logError('LOGIN FAILED: ' + errorMessage, errorDetails);
      toast.error(errorMessage, { duration: 10000 }); // Show toast for 10 seconds
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const formData = new FormData(event.target);
    const username = formData.get('username');
    const email = formData.get('email');
    const password = formData.get('password');
    const confirmPassword = formData.get('confirmPassword');

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.register({
        username,
        email: email || null,
        password
      });
      login(response.data.access_token);
      
      // New users are never admin, so redirect to dashboard
      navigate('/dashboard');
      toast.success('Account created successfully!');
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const formData = new FormData(event.target);
    const identifier = formData.get('identifier');

    try {
      // Use the same magic link system for password reset
      await api.requestAuthLink(identifier);
      toast.success('Password reset link sent! Check your email or SMS.');
      setForgotPassword(false);
      setEmailSent(true);
    } catch (error) {
      console.error('Password reset failed:', error);
      toast.error('Failed to send password reset link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <Mail className="w-6 h-6 text-green-600" />
            </div>
            <CardTitle>Check Your Messages</CardTitle>
            <CardDescription>
              We've sent you an authentication link. Click the link in your email or SMS to sign in.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={() => {
                setEmailSent(false);
                setForgotPassword(false);
              }}
              data-testid="back-to-auth-btn"
            >
              Back to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (forgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full opacity-20 blur-3xl animate-pulse"></div>
          <div className="absolute top-1/2 -left-40 w-96 h-96 bg-blue-500 rounded-full opacity-20 blur-3xl animate-pulse delay-1000"></div>
        </div>

        {/* Header */}
        <header className="relative z-10 bg-white/10 backdrop-blur-md border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center space-x-2 text-white hover:opacity-80 transition-opacity">
                <QrCodeIcon className="h-8 w-8" />
                <span className="text-2xl font-bold">iComment.app</span>
              </Link>
            </div>
          </div>
        </header>

        <div className="relative z-10 flex items-center justify-center p-4 pt-20">
          <Card className="w-full max-w-md shadow-2xl border-0">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold">Reset Password</CardTitle>
              <CardDescription>
                Enter your email address to receive a reset link
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="identifier">Email Address</Label>
                  <Input
                    id="identifier"
                    name="identifier"
                    type="email"
                    placeholder="your@email.com"
                    required
                    data-testid="forgot-password-identifier"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={isLoading}
                  data-testid="send-reset-link-btn"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
                <Button 
                  type="button"
                  variant="outline" 
                  className="w-full" 
                  onClick={() => setForgotPassword(false)}
                  data-testid="back-to-signin-btn"
                >
                  Back to Sign In
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full opacity-20 blur-3xl animate-pulse"></div>
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-blue-500 rounded-full opacity-20 blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-indigo-500 rounded-full opacity-20 blur-3xl animate-pulse delay-2000"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2 text-white hover:opacity-80 transition-opacity">
              <QrCodeIcon className="h-8 w-8" />
              <span className="text-2xl font-bold">iComment.app</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-white hover:bg-white/10">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="relative z-10 flex items-center justify-center p-4 pt-12 pb-16">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4 w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <QrCodeIcon className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Welcome to iComment
            </CardTitle>
            <CardDescription className="text-base mt-2">
              Sign in to manage your QR codes and messages
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="link" data-testid="auth-tab-link">Link</TabsTrigger>
                <TabsTrigger value="login" data-testid="auth-tab-login">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="auth-tab-register">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="link" className="space-y-4">
                <form onSubmit={handleAuthLinkRequest} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="identifier">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="identifier"
                        name="identifier"
                        type="email"
                        placeholder="your@email.com"
                        required
                        className="pl-10"
                        data-testid="auth-identifier-input"
                      />
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isLoading}
                    data-testid="send-auth-link-btn"
                  >
                    {isLoading ? 'Sending...' : 'Send Sign-In Link'}
                  </Button>
                </form>
                <p className="text-sm text-gray-600 text-center">
                  We'll send you a secure link that expires in 48 hours
                </p>
              </TabsContent>

              <TabsContent value="login" className="space-y-4">
                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-identifier">Username or Email</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="login-identifier"
                        name="identifier"
                        type="text"
                        placeholder="username or email"
                        required
                        className="pl-10"
                        data-testid="login-identifier-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="login-password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Your password"
                        required
                        className="pl-10 pr-10"
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowPassword(!showPassword)}
                        data-testid="toggle-password-visibility"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <button
                      type="button"
                      className="text-sm text-blue-600 hover:text-blue-800 underline"
                      onClick={() => setForgotPassword(true)}
                      data-testid="forgot-password-link"
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isLoading}
                    data-testid="login-submit-btn"
                  >
                    {isLoading ? 'Signing In...' : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register" className="space-y-4">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-username">Username</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="register-username"
                        name="username"
                        type="text"
                        placeholder="Choose a username"
                        required
                        className="pl-10"
                        data-testid="register-username-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email (Optional)</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="register-email"
                        name="email"
                        type="email"
                        placeholder="your@email.com"
                        className="pl-10"
                        data-testid="register-email-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="register-password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Create a password"
                        required
                        className="pl-10 pr-10"
                        data-testid="register-password-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowPassword(!showPassword)}
                        data-testid="toggle-register-password-visibility"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-confirm-password">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        id="register-confirm-password"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        required
                        className="pl-10 pr-10"
                        data-testid="register-confirm-password-input"
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        data-testid="toggle-confirm-password-visibility"
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isLoading}
                    data-testid="register-submit-btn"
                  >
                    {isLoading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      
      {/* Error Log Panel */}
      {errorLog.length > 0 && (
        <div className="fixed bottom-4 right-4 w-96 max-h-96 overflow-hidden bg-white rounded-lg shadow-2xl border-2 border-red-500 z-50 animate-pulse">
          <div className="bg-red-500 text-white px-4 py-2 flex justify-between items-center">
            <h3 className="font-bold text-sm">🔍 Sign-In Error Log ({errorLog.length})</h3>
            <div className="flex gap-2">
              <button 
                onClick={() => setShowErrorLog(!showErrorLog)}
                className="text-white hover:text-red-100 text-xs font-medium px-2 py-1 bg-red-600 rounded"
              >
                {showErrorLog ? 'Hide' : 'Show'}
              </button>
              <button 
                onClick={clearErrorLog}
                className="text-white hover:text-red-100 text-xs font-medium px-2 py-1 bg-red-600 rounded"
              >
                Clear
              </button>
            </div>
          </div>
          {lastError && (
            <div className="bg-red-100 px-4 py-2 border-b border-red-200">
              <p className="text-red-800 font-semibold text-sm">Last Error: {lastError}</p>
              <button 
                onClick={() => {
                  localStorage.clear();
                  window.location.reload();
                }}
                className="mt-2 text-xs bg-orange-500 text-white px-3 py-1 rounded hover:bg-orange-600"
              >
                Clear Cache & Reload
              </button>
            </div>
          )}
          {showErrorLog && (
            <div className="p-3 max-h-80 overflow-y-auto bg-gray-50 text-xs font-mono">
              {errorLog.map((entry, idx) => (
                <div key={idx} className="mb-3 pb-3 border-b border-gray-300 last:border-0">
                  <div className="text-gray-600 text-[10px] mb-1">{entry.timestamp}</div>
                  <div className="text-red-700 font-semibold mb-1">{entry.message}</div>
                  <pre className="text-gray-700 text-[10px] whitespace-pre-wrap break-words bg-white p-2 rounded border max-h-40 overflow-auto">
                    {entry.details}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}